import React, { createContext, useContext, useMemo, useRef } from 'react';

import { ProviderId } from '../../entities/provider';

// The single unified project list across all providers — sourced from GET /api/user/projects
// (see widgets/Layout.tsx). `provider` defaults to 'jira' when absent (pre-multi-provider entries).
export type ProjectLike = {
  projectId?: string;
  id?: string;
  key?: string;
  name?: string;
  provider?: ProviderId;
  [k: string]: any;
};

type ProjectsContextValue = {
  projects: ProjectLike[];
  byId: Record<string, ProjectLike>;
  isReady: boolean; // мы хоть раз получили данные
  isStale: boolean; // сейчас показываем прошлые (идёт загрузка/рефетч)
  pendingJiraCount: number; // selected Jira projects excluded because they have no mapId yet
};

const EMPTY: ProjectsContextValue = Object.freeze({
  projects: [],
  byId: {},
  isReady: false,
  isStale: false,
  pendingJiraCount: 0,
});

const ProjectsContext = createContext<ProjectsContextValue>(EMPTY);

function toArraySafe(value: unknown): ProjectLike[] {
  return Array.isArray(value) ? (value as ProjectLike[]) : [];
}

function getProjectId(p: ProjectLike): string | null {
  const id = p.projectId ?? p._id ?? p.key;
  return typeof id === 'string' && id.length ? id : null;
}

function buildIndex(projects: ProjectLike[]) {
  const byId: Record<string, ProjectLike> = {};
  for (const p of projects) {
    const id = getProjectId(p);
    if (id) byId[id] = p;
  }
  return byId;
}

export const ProjectsProvider: React.FC<{
  value?: unknown;
  isLoading?: boolean;
  pendingJiraCount?: number;
  children: React.ReactNode;
}> = ({ value, isLoading = false, pendingJiraCount = 0, children }) => {
  // Храним последнее “хорошее” значение (не пустое)
  const lastGoodRef = useRef<ProjectLike[] | null>(null);
  // Track whether at least one fetch has completed (isLoading went true → false)
  const fetchedOnceRef = useRef(false);
  if (!isLoading) fetchedOnceRef.current = true;

  const incoming = toArraySafe(value);

  // Определяем, считать ли incoming “валидным”
  const hasIncoming = incoming.length > 0;

  // Обновляем lastGood только если пришли реальные данные
  if (hasIncoming) {
    lastGoodRef.current = incoming;
  }

  const shouldUseLastGood =
    (isLoading && !hasIncoming && lastGoodRef.current && lastGoodRef.current.length > 0) ||
    (!hasIncoming && lastGoodRef.current && lastGoodRef.current.length > 0 && value == null);

  const projectsToUse = shouldUseLastGood ? lastGoodRef.current! : incoming;

  const ctxValue = useMemo<ProjectsContextValue>(() => {
    const projects = projectsToUse ?? [];
    return {
      projects,
      byId: buildIndex(projects),
      isReady: fetchedOnceRef.current,
      isStale: shouldUseLastGood,
      pendingJiraCount,
    } as any;
  }, [projectsToUse, shouldUseLastGood, pendingJiraCount]);
  return <ProjectsContext.Provider value={ctxValue}>{children}</ProjectsContext.Provider>;
};

export function useProjects(): ProjectLike[] {
  return useContext(ProjectsContext).projects;
}

export function useProjectsMeta() {
  const { isReady, isStale, pendingJiraCount } = useContext(ProjectsContext);
  return { isReady, isStale, pendingJiraCount };
}

export function useProjectById(projectId?: string | null): ProjectLike | null {
  const { byId } = useContext(ProjectsContext);
  if (!projectId) return null;
  return byId[projectId] ?? null;
}
